import { Currency } from '@ts/components/Currency';
import { TextCounter } from '@ts/components/TextCounter';
import { ImageUploader } from '@ts/components/ImageUploader';
import { VideoUploader } from '@ts/components/VideoUploader';
import { Modal } from '@ts/components/Modal';
import { storage } from '@ts/utils/';
import { VideoPlayer } from '@ts/components/VideoPlayer';
import { Share } from '@ts/components/Share';
import { Notifications } from '@ts/services/Notifications';

let originalVideoId, originalVideoStatus;

export const init = () => {
    setHTMXListeners();
    setInteractiveElements();
}

 const setHTMXListeners = () => {
    document.body.addEventListener('htmx:afterSwap', setInteractiveElements);
    document.body.addEventListener('htmx:historyRestore', setInteractiveElements);
}

const setInteractiveElements = () => {
    setDisplayName();
    setCurrency();
    setCounter();
    setShopPhotoUploader(); 
    setViewVideoTools();
    setEditVideoTools();
    setShareSite();
    checkShopVideoStatus();
} 

const setCounter = () => {
    TextCounter.create('.max-character-count', {
        warningThreshold: 10
    });
}

const setCurrency = () => {
    Currency.create('.currency');
}

const stripPossessive = (displayName) => {
    if (!displayName) return '';
    return displayName.replace(/['`'’‘ʼʾˈʹ＇´Ꞌꞌ](?:s)?$/i, '');
}

const handlePossesive = (displayName) => {
    if (!displayName) return '';
    const strippedDisplayName = stripPossessive(displayName);
    const possessiveSuffix = /s$/i.test(strippedDisplayName) ? "'" : "'s";
    return `${strippedDisplayName}${possessiveSuffix}`;
}

const setDisplayName = () => {
    const displayName = document.getElementById('DisplayName');
    if (!displayName) return;
    displayName.addEventListener('input', (e) => {
        const target = e.target;
        const value = target.value;
        const displayNamePreview = target.closest('.form-group').querySelector('.display-name-preview');
        
        if (displayNamePreview) {
            const processedPossessive = handlePossesive(value);
            displayNamePreview.textContent = processedPossessive;
        }
    });
}

const updateButtonLabel = (uploaderInput) => {
    const uploadedLabel = uploaderInput.dataset.uploadedLabel || 'Change';
    const buttonLabel = uploaderInput.closest('.buttons').querySelector('.button-label');
    if (buttonLabel) {
        buttonLabel.textContent = uploadedLabel;
    }
}

const setShopPhotoUploader = () => {
    const uploaderInput = document.getElementById('shop-photo');
    if (!uploaderInput) return;
    ImageUploader.create(uploaderInput, {
        uploadUrl: uploaderInput.dataset.uploadImageUrl || '',
        cropperMode: 'modal',
        fileSizeLimit: 15 * 1024 * 1024, // 15 MB
        inputFileName: uploaderInput.dataset.inputFileName || 'ShopPhoto',
        croppieOptions: {
            viewport: { width: 295, height: 295, type: 'square' },
            boundary: { width: 310, height: 310 }
        },
        onSave: (data) => { 
            updateButtonLabel(uploaderInput);
            handleImageUpload(data) 
        },
        onError: (error) => {
            Modal.createAlertModal({
                title: 'Image Upload Failed',
                message: error,
                closeBtnLabel: 'Close'
            });
        }
    }); 

    const logoUploaderTriggers = document.querySelectorAll('.upload-photo-checklist');
    logoUploaderTriggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            uploaderInput.click();
        });
    }); 
}

const handleImageUpload = async ({ success, viewUrl, file }) => {
    const containerWrapper = document.getElementById('shop-photo')?.closest('.media-upload-container');
    if (!containerWrapper) return;
    toggleBackgroundImage(containerWrapper, viewUrl);
    toggleUploadState(containerWrapper, 'uploaded');
    const fieldValidationError = containerWrapper.querySelector('.field-validation-error');
    if (fieldValidationError) fieldValidationError.innerHTML = '';
}

const setViewVideoTools = () => {
    const videoIdInput = document.getElementById('video-player');
    if (!videoIdInput) return;
    const videoStatus = videoIdInput.dataset.videoStatus;
    const videoId = videoIdInput.dataset.mediaId;
    checkVideoBackgroundImages(videoStatus, videoId);
    setPlayVideo();
}

const setEditVideoTools = () => {
    const originalVideoIdInput = document.getElementById('VideoId');
    const originalVideoStatusInput = document.getElementById('VideoStatus');
    if (!originalVideoIdInput || !originalVideoStatusInput) return;
    const originalVideoId = originalVideoIdInput.value;
    const originalVideoStatus = originalVideoStatusInput.value;
    checkVideoBackgroundImages(originalVideoStatus, originalVideoId);
    setShopVideoUploader();
    setShopDeleteVideo();
}

const checkVideoBackgroundImages = (videoStatus, videoId) => {
    if (videoStatus === 'Complete') {
        setVideoBackgroundImage(videoId);
    }
}

const setVideoBackgroundImage = (videoId) =>{
    const containerWrapper = document.querySelector('.shop-video.media-upload-container');
    if (!containerWrapper) return;
    VideoPlayer.getThumbnail(videoId).then((thumbnailUrl) => {
        toggleBackgroundImage(containerWrapper, thumbnailUrl);
    });
}

const setShopVideoUploader = () => {

    const videoUploaderButton = document.getElementById('shop-video');
    if (!videoUploaderButton) return;

    const uploadUrl = videoUploaderButton.dataset.uploadVideoUrl || '';
    const token = videoUploaderButton.closest('form').querySelector('input[name="__RequestVerificationToken"]').value || '';

    const uploader = VideoUploader.create(videoUploaderButton, {
        uploadUrl: uploadUrl,
        fileSizeLimit: 10000000000,
        token: token,
        onStart: ()=> handleVideoUploadstart(),
        onSuccess: (data) => {
            updateButtonLabel(videoUploaderButton);
            handleVideoUpload(data);
        },
        onError: (error) => handleVideoError(error)
    });
}

const enableSubmitButton = () => {
    const submitBtn = document.getElementById('animated-button-0');
    if (!submitBtn) return;
    submitBtn.disabled = false;
} 

const disableSubmitButton = () => {
    const submitBtn = document.getElementById('animated-button-0');
    if (!submitBtn) return;
    submitBtn.disabled = true;
}   

const handleVideoUploadstart = () => {
    disableSubmitButton();
}

const updateVideoPlayerMediaId = (videoId) => {
    const videoPlayer = document.getElementById('video-player');
    if (!videoPlayer) return;
    videoPlayer.dataset.mediaId = videoId;
}

const displayVideoPlayer = () => {
    const videoPlayer = document.getElementById('video-player');
    const videoPlayButton = document.getElementById('play-video');
    if (!videoPlayer || !videoPlayButton) return;
    videoPlayer.removeAttribute('hidden');
    videoPlayButton.removeAttribute('hidden');
}

const disableVideoPlayer = () => {
    const videoPlayer = document.getElementById('video-player');
    const videoPlayButton = document.getElementById('play-video');
    if (!videoPlayer || !videoPlayButton) return;
    videoPlayer.setAttribute('hidden', 'true');
    videoPlayButton.setAttribute('hidden', 'true');
}

const handleVideoUpload = async (data) => {
    enableVideoPlayerAfterUpload(data.videoId);
    enableSubmitButton();
}

const handleVideoError = (error) => {
    Modal.createAlertModal({
        title: 'Video Upload Failed',
        message: error,
        closeBtnLabel: 'Close'
    });
    enableSubmitButton();
}

const toggleBackgroundImage = (container, imageUrl) => {
    container.style.backgroundImage = `url('${imageUrl}')`;
}

const setShopDeleteVideo = () => {
    const deleteVideoBtn = document.querySelector('a.delete-video');
    if (!deleteVideoBtn) return;
    deleteVideoBtn.addEventListener('click', (e) => {
        e.preventDefault();

        Modal.createConfirmModal({
            title: 'Delete Video',
            message: 'Are you sure you want to delete this video? This action cannot be undone.',
            confirmText: 'Delete',
            cancelText: 'Cancel',
            onConfirm: async () => {
                const containerWrapper = deleteVideoBtn.closest('.media-upload-container');
                handleDeleteVideo(containerWrapper);
            }
        });
    });
}

const handleDeleteVideo = (containerWrapper) => {
    containerWrapper.style.removeProperty("background-image");            
    toggleUploadState(containerWrapper, 'default');
    document.getElementById('VideoId').value = '';
    document.getElementById('VideoStatus').value = '';
    disableVideoPlayer();
}

const toggleUploadState = (container, state) => {
    container.dataset.viewMode = state;
}

const setPlayVideo = () => {
    const playVideoButton = document.getElementById('play-video');
    if (!playVideoButton) return;
    playVideoButton.addEventListener('click', (e) => {
        e.preventDefault();
        openShopVideoModal();
    });
}

const openShopVideoModal = () => {
    const shopVideoModal = document.getElementById('shop-video-modal');
    if (!shopVideoModal) return;
    Modal.create(shopVideoModal, {
        modalSize: 'modal-auto',
        headless: true,
        onOpen: () => {
            setShopVideoPlayer();
        }
    });
}

const setShopVideoPlayer = () => {
    const videoIdInput = document.getElementById('video-player');
    if (!videoIdInput) return;

    VideoPlayer.create('#video-player', {
        mediaId: videoIdInput.dataset.mediaId
    });
}

const checkShopVideoStatus = () => {
    const videoPlayer = document.getElementById('video-player');
    const isProcessingVideo = document.querySelector('.shop-video[data-view-mode="InProgress"]');
    if (!isProcessingVideo || !videoPlayer) return;
    const subscriptions = [
        {
            methodName: "ReceiveShopVideoStatus",
            handler: (videoId, videoStatus) => {
                const videoIdInput = document.getElementById('video-player');
                if (videoIdInput.dataset.mediaId !== videoId) return;
                const containerWrapper = document.querySelector('.shop-video.media-upload-container');
                toggleUploadState(containerWrapper, 'Complete');
                setVideoBackgroundImage(videoId);
                displayVideoPlayer();
            }
        }
    ];
    const notificationUrl = videoPlayer.dataset.notificationHubUrl;
    const checkforVideoStatus = Notifications.create(notificationUrl, subscriptions);
}

const setShareSite = () => {
    // share-btn
    const shareBtns = document.querySelectorAll('.manage-fundraiser .share-btn');
    shareBtns.forEach((btn) => {
        const webshare = Share.create(btn, {
            url: btn.dataset.shareUrl,
            text: btn.dataset.shareText,
            onUnsupported: () => {
                btn.setAttribute('hidden', true);
            }
        });
    });
}